/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useTheme } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@material-ui/core";
import ChatIcon from "@material-ui/icons/Chat";

import SpotLegsRow from "../components/SpotLegsRow";
import RightDrawer from "./RightDrawer";
import CarrierSpotLegDetails from "./CarrierSpotLegDetails";
import UpsSpotLegDetails from "./UpsSpotLegDetails";
import StyledNoResults from "../components/common/StyledNoResults";
import StyledOverlay from "../components/common/StyledOverlay";
import Notification from "../components/common/Notification";

import { isCarrier } from "../helpers/goTrackUserUtils";
import types from "../helpers/types";

import { updateSpotFiltersV2 } from "../slices/spotFiltersSlice";

import { fetchFeatureFlag } from "../slices/featureFlagsSlice";

import useThrottle from "../hooks/useThrottle";
import { fetchCarriers } from "../slices/carriersSlice";

const StyledTablePagination = styled(TablePagination)`
  flex: 0 0 52px;
  border-top: 2px solid ${(props) =>
    props.theme.palette.background.lightGrey} !important;
  border-bottom: 2px solid ${(props) =>
    props.theme.palette.grey[300]} !important;};
  overflow: hidden !important;
`;

const StyledTableContainer = styled(TableContainer)`
  flex: 1 0 auto;
  height: 0px;
`;

const StyledTableCell = styled(TableCell)`
  border-bottom: none !important;
`;

const GOTRACK_LIMIT = window.env.GOTRACK_LIMIT;
const AUTO_REFRESH_GOSPOT = window.env.AUTO_REFRESH_GOSPOT;

let autoRefreshTimeout;

const SpotLegsTable = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const rowsPerPage = parseInt(GOTRACK_LIMIT, 10);

  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const filters = useSelector((state) => state.spotFilters);
  const sortType = parseInt(filters.sortType, 10);
  const sortDirection = parseInt(filters.sortDirection, 10);
  const spotLegDetails = useSelector((state) => state.spotLegDetails.spotLeg);
  const spotLegRequestStatus = useSelector(
    (state) => state.spotLegDetails.requestStatus
  );

  const { currentPage } = filters;

  const spotLegs = useSelector((state) => state.spotLegs);
  const spotLegsResults = spotLegs.spotLegs;
  const { totalCount, isFetching } = spotLegs;
  const parsedSortDirection =
    sortDirection !== types.spotSortDirection.descending ? "asc" : "desc";

  const user = useSelector((state) => state.goTrackUser.goTrackUser);
  const isUserCarrier = isCarrier(user.roles);
  const sessionHasEnded = useSelector(
    (state) => state.goTrackUser.sessionHasEnded
  );

  const {
    claimSpotLegRequestStatus,
    removeAssignedUserRequestStatus,
    updateSpotLegRequestStatus,
    processClaimRequestStatus,
    removeClaimRequestStatus,
    assignSpotLegWithoutClaimRequestStatus,
  } = useSelector((state) => state.updateSpotLeg);

  const apiErrorHasOccurred = useSelector(
    (state) => state.goTrackUser.apiErrorHasOccurred
  );

  const handleChangePage = (event, newPage) => {
    dispatch(updateSpotFiltersV2({ filters: { currentPage: newPage }, history }));
  };

  const handleSort = (sortTypeChange) => {
    let updatedFilters;
    if (sortType !== sortTypeChange) {
      updatedFilters = {
        sortType: sortTypeChange,
        sortDirection: types.spotSortDirection.ascending,
      };
    } else if (sortType === sortTypeChange) {
      if (sortDirection === types.spotSortDirection.ascending) {
        updatedFilters = {
          sortType,
          sortDirection: types.spotSortDirection.descending,
        };
      } else {
        updatedFilters = { sortType: null, sortDirection: null };
      }
    }

    dispatch(
      updateSpotFiltersV2({
        filters: updatedFilters,
        history,
      })
    );
  };

  const handleNotification = (notification) => {
    if (notification) {
      setNotificationMessage(notification);
      setNotificationOpen(true);
    } else {
      setNotificationOpen(false);
    }
  };

  const refetchSpotLegs = () => {
    dispatch(
      updateSpotFiltersV2({
        filters,
        history,
      })
    );
  };

  useEffect(() => {
    if (autoRefreshTimeout) {
      clearTimeout(autoRefreshTimeout);
    }
    if (
      !spotLegDetails &&
      !filters.searchTerm &&
      !sessionHasEnded &&
      !apiErrorHasOccurred
    ) {
      autoRefreshTimeout = setTimeout(refetchSpotLegs, AUTO_REFRESH_GOSPOT);

      return () => {
        if (autoRefreshTimeout) {
          clearTimeout(autoRefreshTimeout);
        }
      };
    }
  }, [spotLegDetails, filters, sessionHasEnded, apiErrorHasOccurred]);

  // Need to throttle in the event that a user both unassigns someone
  // from a leg and makes a status change with a single save to
  // prevent two spot legs fetches being made
  const refetchSpotLegsThrottled = useThrottle(refetchSpotLegs, 1000);

  useEffect(() => {
    if (claimSpotLegRequestStatus === types.requestStatuses.success) {
      refetchSpotLegsThrottled();
    }
  }, [dispatch, claimSpotLegRequestStatus]);

  useEffect(() => {
    if (updateSpotLegRequestStatus === types.requestStatuses.success) {
      refetchSpotLegsThrottled();
    }
  }, [dispatch, updateSpotLegRequestStatus]);

  useEffect(() => {
    if (removeAssignedUserRequestStatus === types.requestStatuses.success) {
      refetchSpotLegsThrottled();
    }
  }, [dispatch, removeAssignedUserRequestStatus]);

  useEffect(() => {
    if (processClaimRequestStatus === types.requestStatuses.success) {
      refetchSpotLegsThrottled();
    }
  }, [dispatch, processClaimRequestStatus]);

  useEffect(() => {
    if (removeClaimRequestStatus === types.requestStatuses.success) {
      refetchSpotLegsThrottled();
    }
  }, [dispatch, removeClaimRequestStatus]);

  useEffect(() => {
    if (
      assignSpotLegWithoutClaimRequestStatus === types.requestStatuses.success
    ) {
      refetchSpotLegsThrottled();
    }
  }, [dispatch, assignSpotLegWithoutClaimRequestStatus]);

  useEffect(() => {
    dispatch(fetchCarriers());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      !spotLegDetails &&
      filters.spotLegId &&
      spotLegRequestStatus != types.requestStatuses.initial
    ) {
      dispatch(updateSpotFiltersV2({ filters: { spotLegId: null }, history }));
    }
  }, [spotLegDetails]);

  return (
    <>
      {isFetching && <StyledOverlay />}
      {spotLegDetails?.spotLeg && (
        <RightDrawer width={700}>
          {isUserCarrier ? (
            <CarrierSpotLegDetails handleNotification={handleNotification} />
          ) : (
            <UpsSpotLegDetails
              handleNotification={handleNotification}
              setUnsavedChanges={setUnsavedChanges}
              unsavedChanges={unsavedChanges}
              setUnsavedModalOpen={setUnsavedModalOpen}
              unsavedModalOpen={unsavedModalOpen}
            />
          )}
        </RightDrawer>
      )}
      {spotLegsResults.length > 0 ? (
        <>
          <StyledTableContainer theme={theme}>
            <Table stickyHeader style={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="notes-header"
                    style={{ width: "35px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.note}
                      direction={parsedSortDirection}
                      onClick={() => handleSort(types.spotSortTypes.note)}
                      data-testid="note-sort"
                    >
                      <ChatIcon />
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="id-header"
                    style={{ width: "50px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.spotLegsId}
                      direction={parsedSortDirection}
                      onClick={() => handleSort(types.spotSortTypes.spotLegsId)}
                      data-testid="spot-legs-id-sort"
                    >
                      ID
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="type-header"
                    style={{ width: "45px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.creationType}
                      direction={parsedSortDirection}
                      onClick={() =>
                        handleSort(types.spotSortTypes.creationType)
                      }
                      data-testid="creation-type-sort"
                    >
                      Type
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="window-header"
                    style={{ width: "65px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.window}
                      direction={parsedSortDirection}
                      onClick={() => handleSort(types.spotSortTypes.window)}
                      data-testid="window-sort"
                    >
                      Window
                    </TableSortLabel>
                  </StyledTableCell>
                  {!isUserCarrier && (
                    <StyledTableCell
                      theme={theme}
                      align="left"
                      key="status-header"
                      style={{ width: "75px" }}
                      size="small"
                    >
                      <TableSortLabel
                        active={sortType === types.spotSortTypes.status}
                        direction={parsedSortDirection}
                        onClick={() => handleSort(types.spotSortTypes.status)}
                        data-testid="status-sort"
                      >
                        Status
                      </TableSortLabel>
                    </StyledTableCell>
                  )}
                    <StyledTableCell
                      theme={theme}
                      align="left"
                      key="ups-route-id-header"
                      style={{ width: "105px" }}
                      size="small"
                    >
                      <TableSortLabel
                        active={sortType === types.spotSortTypes.upsRouteId}
                        direction={parsedSortDirection}
                        onClick={() => handleSort(types.spotSortTypes.upsRouteId)}
                        data-testid="ups-route-id-sort"
                      >
                        Route Id
                      </TableSortLabel>
                    </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="route-code-header"
                    style={{ width: "105px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.routeCode}
                      direction={parsedSortDirection}
                      onClick={() => handleSort(types.spotSortTypes.routeCode)}
                      data-testid="route-code-sort"
                    >
                      Route Code
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="move-origin-header"
                    style={{ width: "110px", whiteSpace: "nowrap" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.movementOrigin}
                      direction={parsedSortDirection}
                      onClick={() =>
                        handleSort(types.spotSortTypes.movementOrigin)
                      }
                      data-testid="move-origin-sort"
                    >
                      Move Origin
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="scheduled-departure-header"
                    style={{ width: "125px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={
                        sortType === types.spotSortTypes.scheduledDeparture
                      }
                      direction={parsedSortDirection}
                      onClick={() =>
                        handleSort(types.spotSortTypes.scheduledDeparture)
                      }
                      data-testid="sched-departure-sort"
                    >
                      Sched Dep
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="move-destination-header"
                    style={{ width: "110px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={
                        sortType === types.spotSortTypes.movementDestination
                      }
                      direction={parsedSortDirection}
                      onClick={() =>
                        handleSort(types.spotSortTypes.movementDestination)
                      }
                      data-testid="move-destination-sort"
                    >
                      Move Dest
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="scheduled-arrival-header"
                    style={{ width: "125px", whiteSpace: "nowrap" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.scheduledArrival}
                      direction={parsedSortDirection}
                      onClick={() =>
                        handleSort(types.spotSortTypes.scheduledArrival)
                      }
                      data-testid="sched-arrival-sort"
                    >
                      Sched Arr
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="origin-city-header"
                    style={{ width: "100px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.originCity}
                      direction={parsedSortDirection}
                      onClick={() => handleSort(types.spotSortTypes.originCity)}
                      data-testid="origin-city-sort"
                    >
                      Origin City
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="origin-state-header"
                    style={{ width: "30px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.originState}
                      direction={parsedSortDirection}
                      onClick={() =>
                        handleSort(types.spotSortTypes.originState)
                      }
                      data-testid="origin-state-sort"
                    >
                      ST
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="destination-city-header"
                    style={{ width: "100px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.destinationCity}
                      direction={parsedSortDirection}
                      onClick={() =>
                        handleSort(types.spotSortTypes.destinationCity)
                      }
                      data-testid="destination-city-sort"
                    >
                      Dest City
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="destination-state-header"
                    style={{ width: "30px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.destinationState}
                      direction={parsedSortDirection}
                      onClick={() =>
                        handleSort(types.spotSortTypes.destinationState)
                      }
                      data-testid="destination-state-sort"
                    >
                      ST
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="distance-header"
                    style={{ width: "45px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.distance}
                      direction={parsedSortDirection}
                      onClick={() => handleSort(types.spotSortTypes.distance)}
                      data-testid="distance-sort"
                    >
                      Dist.
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="srv-header"
                    style={{ width: "35px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.serviceLevel}
                      direction={parsedSortDirection}
                      onClick={() =>
                        handleSort(types.spotSortTypes.serviceLevel)
                      }
                      data-testid="srv-sort"
                    >
                      Srv
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="trailer-header"
                    style={{ width: "100px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.trailer}
                      direction={parsedSortDirection}
                      onClick={() => handleSort(types.spotSortTypes.trailer)}
                      data-testid="trailer-sort"
                    >
                      Trailer
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="trailer-type-header"
                    style={{ width: "105px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.trailerType}
                      direction={parsedSortDirection}
                      onClick={() =>
                        handleSort(types.spotSortTypes.trailerType)
                      }
                      data-testid="trailer-type-sort"
                    >
                      Trailer Type
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="driver-type-header"
                    style={{ width: "100px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.driverType}
                      direction={parsedSortDirection}
                      onClick={() => handleSort(types.spotSortTypes.driverType)}
                      data-testid="driver-type-sort"
                    >
                      Driver Type
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="requirements-header"
                    style={{ width: "100px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.requirements}
                      direction={parsedSortDirection}
                      onClick={() =>
                        handleSort(types.spotSortTypes.requirements)
                      }
                      data-testid="requirements-sort"
                    >
                      Requirements
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="assigned-header"
                    style={{ width: "130px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.assignedUser}
                      direction={parsedSortDirection}
                      onClick={() =>
                        handleSort(types.spotSortTypes.assignedUser)
                      }
                      data-testid="assigned-sort"
                    >
                      Assigned
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="load-origin-header"
                    style={{ width: "100px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.loadOrigin}
                      direction={parsedSortDirection}
                      onClick={() => handleSort(types.spotSortTypes.loadOrigin)}
                      data-testid="load-origin-sort"
                    >
                      Load Origin
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="load-destination-header"
                    style={{ width: "100px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={sortType === types.spotSortTypes.loadDestination}
                      direction={parsedSortDirection}
                      onClick={() =>
                        handleSort(types.spotSortTypes.loadDestination)
                      }
                      data-testid="load-destination-sort"
                    >
                      Load Dest
                    </TableSortLabel>
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {spotLegsResults.map((row) => {
                  return (
                    <SpotLegsRow
                      row={row}
                      isUserCarrier={isUserCarrier}
                      key={`spot-leg-row-${row.spotLegsId}`}
                      selected={row.spotLegsId === spotLegDetails?.spotLegsId}
                      setUnsavedModalOpen={setUnsavedModalOpen}
                      unsavedChanges={unsavedChanges}
                      handleNotification={handleNotification}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </StyledTableContainer>
          {!isFetching && (
            <StyledTablePagination
              component="div"
              theme={theme}
              count={totalCount}
              rowsPerPageOptions={[rowsPerPage]}
              rowsPerPage={rowsPerPage}
              page={currentPage}
              onChangePage={handleChangePage}
            />
          )}{" "}
        </>
      ) : (
        <StyledNoResults theme={theme}>
          {!isFetching && "No Results"}
        </StyledNoResults>
      )}
      <Notification
        message={notificationMessage}
        open={notificationOpen}
        setOpen={setNotificationOpen}
      />
    </>
  );
};

export default SpotLegsTable;
